import { defineCommand, defineScope, serverUndoCommands } from '@dataroom/interaction';
import type { InboxAction } from '@dataroom/core/inbox';
import { defineAppCommands } from '../../extensions/commands';
import { t } from '../../lib/i18n';
import { localizeInboxPage } from './localize';
export type InboxContext = {
  step: (d: 1 | -1) => void;
  /** `x` toggles the current item; ⇧x (`range`) selects from the last toggled item to the current one. */
  select: (range: boolean) => void;
  /** Escape: clear a multi-selection (false when nothing is selected). */
  clearSelection: () => boolean;
  action: (a: InboxAction['action']) => void;
  can: (a: InboxAction['action']) => boolean;
  openFull: () => void;
  create: () => void;
  search: () => void;
  reply: () => void;
};
export const inboxScope = defineScope<InboxContext>({
  id: 'inbox.list',
  kind: 'list',
  title: () => t('inbox.title'),
});
/** What members call the agent's change sets and their approval, in English and German. */
const CHANGE_SET_KEYWORDS = [
  'change sets',
  'change set',
  'changeset',
  'agent changes',
  'änderungspakete',
  'änderungspaket',
];
export default defineAppCommands({
  id: 'inbox.unified',
  loadTitles: () => Promise.all([import('../tasks/messages'), import('../../messages-ready/inbox-unified')]),
  scopes: [inboxScope],
  commands: [
    ...serverUndoCommands(inboxScope),
    defineCommand<InboxContext>({
      id: 'inbox.next',
      scope: inboxScope,
      title: () => t('interaction.cmd.workNext'),
      shortcut: ['j', 'ArrowDown'],
      repeat: true,
      run: (c) => c.step(1),
    }),
    defineCommand<InboxContext>({
      id: 'inbox.previous',
      scope: inboxScope,
      title: () => t('interaction.cmd.workPrevious'),
      shortcut: ['k', 'ArrowUp'],
      repeat: true,
      run: (c) => c.step(-1),
    }),
    defineCommand<InboxContext>({
      id: 'inbox.select',
      scope: inboxScope,
      title: () => t('inbox.cmd.select'),
      shortcut: 'x',
      run: (c) => c.select(false),
    }),
    defineCommand<InboxContext>({
      id: 'inbox.selectAll',
      scope: inboxScope,
      title: () => t('inbox.cmd.selectRange'),
      shortcut: 'shift+x',
      run: (c) => c.select(true),
    }),
    defineCommand<InboxContext>({
      id: 'inbox.clearSelection',
      scope: inboxScope,
      title: () => t('inbox.clear'),
      shortcut: 'escape',
      hidden: true,
      run: (c) => c.clearSelection(),
    }),
    defineCommand<InboxContext>({
      id: 'inbox.complete',
      scope: inboxScope,
      title: () => t('inbox.complete'),
      shortcut: 'e',
      // An Inbox list on a dashboard: "e" completes the item, it does not open the dashboard's editor.
      overrides: ['dashboard.edit'],
      // Triage keys stay in the shortcut sheet while no item is open (muted), instead of vanishing from it.
      listed: 'always',
      when: (c) => c.can('complete') || c.can('read'),
      run: (c) => c.action(c.can('complete') ? 'complete' : 'read'),
    }),
    ...(
      [
        ['snooze', 'h', 'inbox.snooze'],
        ['set-due', 'd', 'inbox.set-due'],
        ['unread', 'u', 'inbox.unread'],
        ['approve', 'mod+enter', 'inbox.approve'],
        ['reject', 'mod+backspace', 'inbox.reject'],
        ['claim', 'p', 'tp.action.claim'],
        ['watch', 'w', 'tp.action.watch'],
        ['delegate', 'shift+a', 'tp.act.delegate'],
        ['set-priority', 'shift+p', 'tp.act.set-priority'],
      ] as const
    ).map(([action, shortcut, title]) =>
      defineCommand<InboxContext>({
        id: `inbox.${action}`,
        scope: inboxScope,
        title: () => t(title),
        shortcut,
        listed: 'always',
        when: (c) => c.can(action),
        run: (c) => c.action(action),
      }),
    ),
    defineCommand<InboxContext>({
      id: 'inbox.assignOrApprove',
      scope: inboxScope,
      title: () => t('inbox.assign'),
      shortcut: 'a',
      listed: 'always',
      when: (c) => c.can('approve') || c.can('assign'),
      run: (c) => c.action(c.can('approve') ? 'approve' : 'assign'),
    }),
    defineCommand<InboxContext>({
      id: 'inbox.open',
      scope: inboxScope,
      title: () => t('inbox.openItem'),
      shortcut: 'Enter',
      run: (c) => c.openFull(),
    }),
    defineCommand<InboxContext>({
      id: 'inbox.create',
      scope: inboxScope,
      title: () => t('inbox.new'),
      shortcut: 'c',
      overrides: ['app.create'],
      run: (c) => c.create(),
    }),
    defineCommand<InboxContext>({
      id: 'inbox.search',
      scope: inboxScope,
      title: () => t('inbox.search'),
      shortcut: '/',
      // In the Inbox "/" searches the Inbox rather than the whole workspace.
      overrides: ['app.search'],
      run: (c) => c.search(),
    }),
    defineCommand<InboxContext>({
      id: 'inbox.reply',
      scope: inboxScope,
      title: () => t('inbox.discussion'),
      shortcut: 'r',
      run: (c) => c.reply(),
    }),
  ],
  providers: [
    // ⌘K: Inbox › Change sets, the approval queue and history of what agents change.
    {
      id: 'inbox.changeSets',
      items: (query) => {
        const q = query.trim().toLowerCase();
        if (q && !CHANGE_SET_KEYWORDS.some((k) => k.startsWith(q) || q.startsWith(k))) return [];
        return [
          {
            id: 'inbox.changeSets',
            title: t('inbox.changeSetsLink'),
            icon: 'sparkle',
            group: t('inbox.title'),
            keywords: CHANGE_SET_KEYWORDS,
            href: '/change-sets',
          },
        ];
      },
    },
    {
      id: 'inbox.items',
      items: async (query) => {
        if (query.trim().length < 2) return [];
        const { api } = await import('../../lib/api');
        const { listInbox } = await import('@dataroom/contracts/inbox');
        const data = await api.call(listInbox, {
          query: { filters: { q: query, assignee: 'me' }, limit: 10 },
        });
        return localizeInboxPage(data).items.map((i) => ({
          id: i.id,
          title: i.title,
          group: t('inbox.title'),
          icon: 'inbox' as const,
          href: `/inbox?item=${encodeURIComponent(i.id)}`,
        }));
      },
    },
  ],
});
